import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { CardType } from './KanbanBoard';

interface CardModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (colId: string, card: CardType) => void;
  colId: string | null;
  card?: CardType;
  allCards?: CardType[]; // For linking
}

const defaultContent = (type: CardType['type']) => {
  if (type === 'vuln') {
    return {
      severity: 'Medium',
      endpoint: '',
      status: 'Needs-Review',
      description: '',
      poc: '',
      customFields: [],
      attachments: [],
      checklist: [],
      links: [],
    };
  } else if (type === 'tool') {
    return { command: '', customFields: [], attachments: [], checklist: [], links: [] };
  } else if (type === 'payload') {
    return { code: '', customFields: [], attachments: [], checklist: [], links: [] };
  } else if (type === 'resource') {
    return { url: '', customFields: [], attachments: [], checklist: [], links: [] };
  }
  return { note: '', customFields: [], attachments: [], checklist: [], links: [] };
};

const CardModal: React.FC<CardModalProps> = ({ open, onClose, onSave, colId, card, allCards }) => {
  const [type, setType] = useState<CardType['type']>(card?.type || 'note');
  const [title, setTitle] = useState(card?.title || '');
  const [content, setContent] = useState<any>(card?.content || defaultContent(type));
  const [color, setColor] = useState(card?.color || '#fff');
  const [tags, setTags] = useState<string[]>(card?.tags || []);
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    if (card) {
      setType(card.type);
      setTitle(card.title);
      setContent(card.content);
      setColor(card.color || '#fff');
      setTags(card.tags || []);
    } else {
      setType('note');
      setTitle('');
      setContent(defaultContent('note'));
      setColor('#fff');
      setTags([]);
    }
  }, [card, open]);

  useEffect(() => {
    if (!card) setContent(defaultContent(type));
    // eslint-disable-next-line
  }, [type]);

  if (!open || !colId) return null;

  // Custom fields logic
  const customFields = content.customFields || [];
  const handleCustomFieldChange = (idx: number, key: string, value: string) => {
    const updated = customFields.map((f: any, i: number) => i === idx ? { key, value } : f);
    setContent((c: any) => ({ ...c, customFields: updated }));
  };
  const handleAddCustomField = () => {
    setContent((c: any) => ({ ...c, customFields: [...(c.customFields || []), { key: '', value: '' }] }));
  };
  const handleRemoveCustomField = (idx: number) => {
    setContent((c: any) => ({ ...c, customFields: customFields.filter((_: any, i: number) => i !== idx) }));
  };

  // Tag logic
  const handleAddTag = () => {
    if (tagInput && !tags.includes(tagInput)) {
      setTags([...tags, tagInput]);
      setTagInput('');
    }
  };
  const handleRemoveTag = (tag: string) => setTags(tags.filter(t => t !== tag));

  // Attachments logic
  const attachments = content.attachments || [];
  const handleAttachment = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setContent((c: any) => ({ ...c, attachments: [...(c.attachments || []), { name: file.name, data: reader.result }] }));
    };
    reader.readAsDataURL(file);
  };
  const handleRemoveAttachment = (idx: number) => {
    setContent((c: any) => ({ ...c, attachments: attachments.filter((_: any, i: number) => i !== idx) }));
  };

  // Checklist logic
  const checklist = content.checklist || [];
  const handleChecklistChange = (idx: number, value: string) => {
    setContent((c: any) => ({ ...c, checklist: checklist.map((item: any, i: number) => i === idx ? { ...item, text: value } : item) }));
  };
  const handleChecklistToggle = (idx: number) => {
    setContent((c: any) => ({ ...c, checklist: checklist.map((item: any, i: number) => i === idx ? { ...item, checked: !item.checked } : item) }));
  };
  const handleAddChecklistItem = () => {
    setContent((c: any) => ({ ...c, checklist: [...(c.checklist || []), { text: '', checked: false }] }));
  };
  const handleRemoveChecklistItem = (idx: number) => {
    setContent((c: any) => ({ ...c, checklist: checklist.filter((_: any, i: number) => i !== idx) }));
  };

  // Card linking logic
  const links = content.links || [];
  const handleAddLink = (cardId: string) => {
    if (!links.includes(cardId)) {
      setContent((c: any) => ({ ...c, links: [...(c.links || []), cardId] }));
    }
  };
  const handleRemoveLink = (cardId: string) => {
    setContent((c: any) => ({ ...c, links: links.filter((id: string) => id !== cardId) }));
  };

  const handleSave = () => {
    const newCard: CardType = {
      id: card?.id || `card-${Date.now()}`,
      type,
      title: title || 'Untitled',
      content,
      color,
      tags,
      createdAt: card?.createdAt || Date.now(),
      updatedAt: Date.now(),
    };
    onSave(colId, newCard);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md relative">
        <button className="absolute top-2 right-2 text-gray-400 hover:text-red-600" onClick={onClose}>×</button>
        <h2 className="text-lg font-bold mb-4">{card ? 'Edit Card' : 'Add Card'}</h2>
        <div className="mb-3 flex gap-2 items-center">
          <label className="block text-sm font-medium mb-1">Type</label>
          <select
            className="border rounded px-2 py-1"
            value={type}
            onChange={e => setType(e.target.value as CardType['type'])}
          >
            <option value="note">📝 Note</option>
            <option value="vuln">🐞 Vulnerability</option>
            <option value="tool">🔧 Tool/Command</option>
            <option value="payload">💥 Payload</option>
            <option value="resource">🔗 Resource/Link</option>
          </select>
          <label className="ml-4 text-xs">Color</label>
          <input type="color" value={color} onChange={e => setColor(e.target.value)} className="w-6 h-6 p-0 border-none bg-transparent" />
        </div>
        <div className="mb-3">
          <label className="block text-sm font-medium mb-1">Title</label>
          <input
            className="w-full border rounded px-2 py-1"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="Card title"
          />
        </div>
        {/* Tag input */}
        <div className="mb-3">
          <label className="block text-sm font-medium mb-1">Tags</label>
          <div className="flex gap-2 flex-wrap mb-1">
            {tags.map(tag => (
              <span key={tag} className="bg-slate-200 rounded px-2 py-0.5 text-xs flex items-center gap-1">
                {tag}
                <button className="ml-1 text-xs text-gray-400 hover:text-red-600" onClick={() => handleRemoveTag(tag)}>×</button>
              </span>
            ))}
            <input
              className="border rounded px-1 py-0.5 text-xs"
              value={tagInput}
              onChange={e => setTagInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddTag(); } }}
              placeholder="Add tag"
              style={{ minWidth: 60 }}
            />
            <button className="btn btn-xs" onClick={handleAddTag}>Add</button>
          </div>
        </div>
        {/* Attachments */}
        <div className="mb-3">
          <label className="block text-sm font-medium mb-1">Attachments</label>
          <input type="file" onChange={handleAttachment} />
          <div className="flex flex-wrap gap-2 mt-2">
            {attachments.map((att: any, idx: number) => (
              <div key={idx} className="flex flex-col items-center border rounded p-1 relative">
                {att.data && att.data.startsWith('data:image') ? (
                  <img src={att.data} alt={att.name} className="w-16 h-16 object-cover rounded" />
                ) : (
                  <span className="text-xs text-slate-600">{att.name}</span>
                )}
                <button className="absolute top-0 right-0 text-xs text-gray-400 hover:text-red-600" onClick={() => handleRemoveAttachment(idx)}>×</button>
              </div>
            ))}
          </div>
        </div>
        {/* Checklist */}
        <div className="mb-3">
          <label className="block text-sm font-medium mb-1">Checklist</label>
          {checklist.map((item: any, idx: number) => (
            <div key={idx} className="flex items-center gap-2 mb-1">
              <input type="checkbox" checked={!!item.checked} onChange={() => handleChecklistToggle(idx)} />
              <input
                className="border rounded px-1 py-0.5 text-xs flex-1"
                value={item.text}
                onChange={e => handleChecklistChange(idx, e.target.value)}
                placeholder="Checklist item"
              />
              <button className="btn btn-xs" onClick={() => handleRemoveChecklistItem(idx)} title="Remove">×</button>
            </div>
          ))}
          <button className="btn btn-xs mt-1" onClick={handleAddChecklistItem}>+ Add Checklist Item</button>
        </div>
        {/* Card linking */}
        <div className="mb-3">
          <label className="block text-sm font-medium mb-1">Linked Cards</label>
          <div className="flex flex-wrap gap-2 mb-1">
            {links.map((id: string) => {
              const linked = allCards?.find(c => c.id === id);
              return linked ? (
                <span key={id} className="bg-blue-100 rounded px-2 py-0.5 text-xs flex items-center gap-1">
                  {linked.title}
                  <button className="ml-1 text-xs text-gray-400 hover:text-red-600" onClick={() => handleRemoveLink(id)}>×</button>
                </span>
              ) : null;
            })}
          </div>
          <select
            className="w-full border rounded px-2 py-1 mt-1"
            value=""
            onChange={e => { handleAddLink(e.target.value); }}
          >
            <option value="">Link another card...</option>
            {allCards?.filter(c => c.id !== card?.id && !links.includes(c.id)).map(c => (
              <option key={c.id} value={c.id}>{c.title}</option>
            ))}
          </select>
        </div>
        {/* Content fields by type */}
        {type === 'note' && (
          <div className="mb-3">
            <label className="block text-sm font-medium mb-1">Note (Markdown supported)</label>
            <textarea
              className="w-full border rounded px-2 py-1 font-mono"
              value={typeof content === 'string' ? content : content.note || ''}
              onChange={e => setContent(e.target.value)}
              rows={4}
              placeholder="Write your note..."
            />
            <div className="mt-2 p-2 bg-slate-50 rounded border text-xs">
              <ReactMarkdown>{typeof content === 'string' ? content : content.note || ''}</ReactMarkdown>
            </div>
          </div>
        )}
        {type === 'vuln' && (
          <div className="mb-3 grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium mb-1">Severity</label>
              <select
                className="w-full border rounded px-2 py-1"
                value={content.severity}
                onChange={e => setContent((c: any) => ({ ...c, severity: e.target.value }))}
              >
                <option value="Critical">Critical</option>
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Status</label>
              <select
                className="w-full border rounded px-2 py-1"
                value={content.status}
                onChange={e => setContent((c: any) => ({ ...c, status: e.target.value }))}
              >
                <option value="Needs-Review">Needs-Review</option>
                <option value="Confirmed">Confirmed</option>
                <option value="Reported">Reported</option>
                <option value="Patched">Patched</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium mb-1">Endpoint</label>
              <input
                className="w-full border rounded px-2 py-1"
                value={content.endpoint}
                onChange={e => setContent((c: any) => ({ ...c, endpoint: e.target.value }))}
                placeholder="/api/login"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium mb-1">Description (Markdown supported)</label>
              <textarea
                className="w-full border rounded px-2 py-1 font-mono"
                value={content.description}
                onChange={e => setContent((c: any) => ({ ...c, description: e.target.value }))}
                rows={2}
                placeholder="Describe the vulnerability..."
              />
              <div className="mt-2 p-2 bg-slate-50 rounded border text-xs">
                <ReactMarkdown>{content.description}</ReactMarkdown>
              </div>
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium mb-1">Proof of Concept (PoC, Markdown supported)</label>
              <textarea
                className="w-full border rounded px-2 py-1 font-mono"
                value={content.poc}
                onChange={e => setContent((c: any) => ({ ...c, poc: e.target.value }))}
                rows={2}
                placeholder="PoC code or steps..."
              />
              <div className="mt-2 p-2 bg-slate-50 rounded border text-xs">
                <ReactMarkdown>{content.poc}</ReactMarkdown>
              </div>
            </div>
          </div>
        )}
        {type === 'tool' && (
          <div className="mb-3">
            <label className="block text-sm font-medium mb-1">Command</label>
            <input
              className="w-full border rounded px-2 py-1 font-mono"
              value={content.command}
              onChange={e => setContent((c: any) => ({ ...c, command: e.target.value }))}
              placeholder="nmap -A target.com"
            />
          </div>
        )}
        {type === 'payload' && (
          <div className="mb-3">
            <label className="block text-sm font-medium mb-1">Payload Code</label>
            <textarea
              className="w-full border rounded px-2 py-1 font-mono"
              value={content.code}
              onChange={e => setContent((c: any) => ({ ...c, code: e.target.value }))}
              rows={2}
              placeholder="<script>alert(1)</script>"
            />
          </div>
        )}
        {type === 'resource' && (
          <div className="mb-3">
            <label className="block text-sm font-medium mb-1">URL</label>
            <input
              className="w-full border rounded px-2 py-1"
              value={content.url}
              onChange={e => setContent((c: any) => ({ ...c, url: e.target.value }))}
              placeholder="https://..."
            />
          </div>
        )}
        {/* Custom fields */}
        <div className="mb-3">
          <label className="block text-sm font-medium mb-1">Custom Fields</label>
          {customFields.map((field: any, idx: number) => (
            <div key={idx} className="flex gap-2 mb-1">
              <input
                className="border rounded px-1 py-0.5 text-xs flex-1"
                value={field.key}
                onChange={e => handleCustomFieldChange(idx, e.target.value, field.value)}
                placeholder="Key"
              />
              <input
                className="border rounded px-1 py-0.5 text-xs flex-1"
                value={field.value}
                onChange={e => handleCustomFieldChange(idx, field.key, e.target.value)}
                placeholder="Value"
              />
              <button className="btn btn-xs" onClick={() => handleRemoveCustomField(idx)} title="Remove">×</button>
            </div>
          ))}
          <button className="btn btn-xs mt-1" onClick={handleAddCustomField}>+ Add Custom Field</button>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button className="btn btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={handleSave}>Save</button>
        </div>
      </div>
    </div>
  );
};

export default CardModal; 